import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import Loading from 'common/services/Loading';
import { useToasts } from 'react-toast-notifications';
import Logger from 'common/services/Logger';
import { ChargeGeofenceListDto, ChargeGeofenceDto, ChargeDto } from 'api/charges/models/ChargeDto';

import { DATE_FORMAT_DEFAUT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, DefaultAssetImage, LOGGER_LOG_TYPE } from 'Config';
import ChargesService from 'api/charges/ChargesService';

import CompactPageLayout from 'common/components/compactPageLayout/CompactPageLayout';
import Button from 'common/components/button/Button';


import { useHistory, useLocation } from 'react-router-dom';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import Box from 'common/components/box/Box';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import colors from 'styles/export/colors.module.scss';
import ReactTooltip from 'react-tooltip';

import styles from './ChargeGeofenceScreen.module.scss'
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { UserProfile } from 'api/account/models/UserProfile';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import Select from 'common/components/select/Select';
import InputGroup from 'common/components/inputGroup/InputGroup';

import { ReactComponent as TrashIcon } from 'assets/svg/trash_no_color.svg';

import AddIcon from 'assets/svg/desktop_add_new.svg';
import { Row, Col } from 'react-flexbox-grid';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import { ChargesSearchCriteria } from 'api/charges/models/ChargesSearchCriteria';
import InputError from 'common/components/inputError/InputError';

type Props = {

};

const ChargeGeofenceScreen: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const location = useLocation();
    const history = useHistory();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [reRender, setReRender] = useState<number>(0);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const imageCacheKey = useRef(new Date().getTime());

    const form = useForm<ChargeGeofenceListDto>({ shouldUnregister: false });
    const { handleSubmit, formState } = form;
    const { errors } = formState;

    const [charges, setCharges] = useState<ChargeDto[]>([]);
    const geofences = useFieldArray<ChargeGeofenceDto>({ control: form.control, name: 'geofences', keyName: 'formId' as any });

    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const [showRemoveGeofenceModal, setShowRemoveGeofenceModal] = useState<boolean>(false);
    const [geofenseToRemove, setGeofenseToRemove] = useState<number | null>(null);

    const { calculation, selectedCharges, geofenceList, listCriteria } = (location.state || {}) as { calculation: string; selectedCharges: ChargeDto[]; geofenceList: SelectValueLabel[]; listCriteria: ChargesSearchCriteria };

    const loadChargesAndGeofences = async (chargesToUse: ChargeDto[]) => {
        setTotalItems(chargesToUse.length);

        const chargesWithGeofences = chargesToUse.map(c => ({
            ...c,
            collapsed: true,
        }));
        setCharges(chargesWithGeofences);

        const calcs = await ChargesService.detectGeofences({
            charges: chargesToUse.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map(x => x.id ?? ''),
            calculation,
        });

        if (calcs) {
            calcs.forEach(g => {
                if (geofences.fields.filter(f => f.chargeId === g.chargeId && f.geofenceId === g.geofenceId).length === 0) {
                    geofences.append({
                        chargeId: g.chargeId,
                        geofenceId: g.geofenceId,
                        percentage: g.percentage,
                        km: g.km,
                        duration: g.duration
                    });
                }
            });
        }
    };

    useEffect(() => {

        (async () => {
            try {
                Loading.show();

                let chargesToUse = selectedCharges || [];
                // Se selectedCharges vier vazio e houver listCriteria → buscar da API
                if ((!selectedCharges || selectedCharges.length === 0) && listCriteria) {
                    const result = await ChargesService.getList({ ...listCriteria, itemsPerPage: 9999999, page: 1 });
                    chargesToUse = result.items || [];
                }

                // Se continua vazio, não faz nada
                if (!chargesToUse || chargesToUse.length === 0) {
                    Loading.hide();
                    return;
                }

                await loadChargesAndGeofences(chargesToUse);

            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't load charges or geofences`, error);
                addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            } finally {
                Loading.hide();
            }
        })();
    }, []);

    const paginatedCharges = React.useMemo(() => {
        const start = (currentPage - 1) * DEFAULT_PAGINATION_ITEMS_PER_PAGE;
        const end = start + DEFAULT_PAGINATION_ITEMS_PER_PAGE;
        return charges.slice(start, end);
    }, [charges, currentPage]);

    const calculateChargesTotalsAndInvalids = () => {
        const geofencesList = form.getValues('geofences') ?? [];
        let invalidChargeId: string | null = null;
        // Soma percentagens por chargeId (já arredondadas)
        const sumsByChargeId = geofencesList.reduce<Record<string, number>>((acc, { chargeId, percentage, geofenceId }) => {
            if (!chargeId || percentage == null) return acc;
            if ((!geofenceId || !percentage) && !invalidChargeId) invalidChargeId = chargeId;
            acc[chargeId] = (acc[chargeId] || 0) + Number(percentage);
            return acc;
        }, {});

        let firstInvalidIndex: number | null = null;

        // Atualiza as charges e marca a primeira inválida
        const updatedCharges = charges.map((c, i) => {
            const total = Number((sumsByChargeId[c.id ?? ''] ?? 0).toFixed(2));
            const overLimit = total > 100;
            if ((overLimit || (invalidChargeId && invalidChargeId == c.id)) && firstInvalidIndex == null) firstInvalidIndex = i;
            return { ...c, percentageOverLimit: overLimit, hasError: (invalidChargeId && c.id && invalidChargeId == c.id) as boolean };
        });

        return { updatedCharges, firstInvalidIndex, geofencesList };
    };

    const validateGeofences = (geofencesList: any[]) => {
        let hasFieldEmptyError = false;
        let hasValueZeroError = false;
        let hasTotalOverHundredError = false;

        geofencesList.forEach((g, i) => {
            const { geofenceId, percentage } = g;

            if (!geofenceId) {
                form.setError(`geofences.${i}.geofenceId`, { type: 'manual', message: t('common.errors.required') });
                hasFieldEmptyError = true;
            }
            if (percentage == null || percentage === 0) {
                form.setError(`geofences.${i}.percentage`, { type: 'manual', message: t('charges.geofences.min_value_greater_than_0') });
                hasValueZeroError = true;
            }
            if (percentage > 100) {
                form.setError(`geofences.${i}.percentage`, { type: 'manual', message: t('common.errors.max_value_100') });
                hasTotalOverHundredError = true;
            }
        });



        return { hasFieldEmptyError, hasValueZeroError, hasTotalOverHundredError };
    };

    const onSubmit = async () => {
        const { updatedCharges, firstInvalidIndex, geofencesList } = calculateChargesTotalsAndInvalids();
        setCharges(updatedCharges);
        const { hasFieldEmptyError, hasValueZeroError, hasTotalOverHundredError } = validateGeofences(geofencesList);

        if (firstInvalidIndex !== null) {
            const pageWithError = Math.floor((firstInvalidIndex ?? 0) / DEFAULT_PAGINATION_ITEMS_PER_PAGE) + 1;
            setCurrentPage(pageWithError);
            setReRender(p => p + 1);

            //erros nas linhas
            if (hasTotalOverHundredError || hasFieldEmptyError || hasValueZeroError) {
                renderErrors(hasFieldEmptyError, hasValueZeroError, hasTotalOverHundredError);
                return;
            }

            //erro no total da soma da percentagem das geofences
            addToast(t('common.errors.max_percentage_100'), { appearance: 'warning' });
            return;


        }
        setIsLoading(true);

        try {
            Loading.show();
            await ChargesService.changeChargeGeofences({ geofences: geofencesList })
            addToast(t('common.messages.record_save_success'), {
                appearance: 'success',
            });
            history.goBack();
            Loading.hide();
        } catch (error) {
            addToast(t('common.messages.record_save_error'), {
                appearance: 'error',
            });
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't create location hotspot`, error);
            Loading.hide();
        } finally {
            setIsLoading(false);
        }
    };

    const onInvalid = () => {
        const { updatedCharges, firstInvalidIndex, geofencesList } = calculateChargesTotalsAndInvalids();
        const { hasFieldEmptyError, hasValueZeroError, hasTotalOverHundredError } = validateGeofences(geofencesList);

        setCharges(updatedCharges);

        if (firstInvalidIndex !== null) {
            const pageWithError = Math.floor(firstInvalidIndex! / DEFAULT_PAGINATION_ITEMS_PER_PAGE) + 1;
            setCurrentPage(pageWithError);
            setReRender(p => p + 1);
        }

        renderErrors(hasFieldEmptyError, hasValueZeroError, hasTotalOverHundredError);

        // ao fazer guardar com as linhas fechadas o form nao atualiza a DOM e por isso dava erro
        //  forçar aqui caso nao tenha erros para guardar
        if (!hasFieldEmptyError && !hasValueZeroError && !hasTotalOverHundredError) {
            onSubmit()
        }
    };


    const renderErrors = (hasFieldEmptyError: boolean, hasValueZeroError: boolean, hasTotalOverHundredError: boolean) => {

        if (hasFieldEmptyError) {
            addToast(t('common.errors.required_fields_empty'), { appearance: 'warning' });
        }
        else if (hasValueZeroError) {
            addToast(t('common.errors.min_percentage_0'), { appearance: 'warning' });
        }
        else if (hasTotalOverHundredError) {
            addToast(t('common.errors.max_percentage_100'), { appearance: 'warning' });
        }
    };

    //render cols
    const renderVehicleCell = (row: ChargeDto) => {
        return (
            <div className={styles.vehicleCell}>
                <div className={styles.imageCell}>
                    <div className={styles.contentImage} style={{ width: '5rem', height: '3rem' }}>
                        <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.vehiclePhotoUrl ? 'url(' + row?.vehiclePhotoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                    </div>
                </div>
                <div className={styles.description}>
                    <div className={styles.bold}>{row.vehicleRegistrationNumber}</div>
                    <div className={styles.smallLabel}>{(row.vehicleBrandName ?? '') + (row.vehicleBrandName && row.vehicleModelName ? ' | ' : '') + (row.vehicleModelName ?? '')}</div>
                </div>
            </div>
        )
    }

    const renderResponsibleCell = (row: ChargeDto) => {
        return (
            <div>
                <AvatarWithText src={row.responsiblePhotoUrl}
                    size="x2"
                    hideAvatar={!row.responsibleName}><div className={styles.bold}>{row.responsibleName || '-'}</div></AvatarWithText>
                <div className={styles.dateFrais}><DateFormat value={row.date} /></div>
            </div>
        )
    }

    const renderSupplierCell = (row: ChargeDto) => {
        return (
            <>
                <div data-tip={row.supplierUrl ? (row.supplierName || '-') : undefined}>
                    {row.supplierUrl && <img src={row.supplierUrl} style={{ height: '2.5rem' }}></img>}
                    {!row.supplierUrl && (row.supplierName || '-')}
                </div>
                <ReactTooltip />
            </>
        )
    }

    const renderCostCell = (row: ChargeDto) => {
        return (
            <div className={styles.costColumn}>
                <div className={styles.moneyColumn}>
                    <MoneyFormat fixedDecimalScale={true} value={row.cost} />
                </div>
                {row.paymentDate ? (<>
                    <div className={styles.secondRow}>
                        <span style={{ width: 'fit-content' }}
                            data-tip={t('charges.payment_date')}>
                            <DateFormat value={row.paymentDate} format={DATE_FORMAT_DEFAUT} />
                        </span>
                    </div>
                    <ReactTooltip />
                </>) : ''}
            </div>
        )
    }
    //end

    const onRowClick = (charge: ChargeDto) => {
        charge.collapsed = !charge.collapsed;
        setReRender(reRender + 1);
    }

    const onRemove = () => {
        if (geofenseToRemove === null) return;
        geofences.remove(geofenseToRemove);
        setGeofenseToRemove(null);
        setShowRemoveGeofenceModal(false);
        setReRender(reRender + 1);
    };

    const onCancelRemove = () => {
        setGeofenseToRemove(null);
        setShowRemoveGeofenceModal(false);
    };

    const onPageChange = (page: number) => {
        setCurrentPage(page);
        // getData();
    }


    return (
        <CompactPageLayout
            title={t(`charges.list.link_geofences_${(calculation ?? '').toLowerCase()}` as any)}
            onBack={() => history.goBack()}
            headerButtons={
                <div className={styles.buttons_container}>
                    <Button text={t('common.cancel')} preset={'secondary'} size={'normal'} onClick={() => history.goBack()} disabled={isLoading} />
                    <Button type='submit' form='charge-geofence-form' text={t('common.save')} size={'normal'} isLoading={isLoading} />
                </div>
            }
        >
            <form id='charge-geofence-form' className={styles.form} onSubmit={handleSubmit(onSubmit, onInvalid)}>
                <div className={styles.scrollableContent} key={`list_charges_${currentPage}`}>
                        <Box>
                            {/* header */}
                            <Row className={styles.lineChargeTitle} >
                                <Col xs={1} />
                                <Col xs={3}>
                                    {t('charges.list.vehicle')}
                                </Col>
                                <Col xs={3}>
                                    {t('charges.responsible_date')}
                                </Col>
                                <Col xs={2}>
                                    {t('common.supplier')}
                                </Col>
                                <Col xs={2}>
                                    {t('common.cost')}
                                </Col>
                                <Col xs={1} />
                            </Row>

                            {/* charges */}
                            {paginatedCharges.map(charge => {
                                return <>
                                    <Row className={`${styles.lineCharge} ${(charge.percentageOverLimit || charge.hasError) ? styles.warningLine : ''}`}
                                        data-tip={charge.percentageOverLimit ? t('common.errors.max_percentage_100') : ''}
                                        onClick={() => onRowClick(charge)} key={charge.id}>
                                        <Col xs={1} style={{ paddingLeft: '1rem' }}>
                                            {charge.collapsed ? <FaAngleDown color={colors.gray} /> : <FaAngleUp color={colors.gray} />}
                                        </Col>
                                        <Col xs={3}>
                                            {renderVehicleCell(charge)}
                                        </Col>
                                        <Col xs={3}>
                                            {renderResponsibleCell(charge)}
                                        </Col>
                                        <Col xs={2}>
                                            {renderSupplierCell(charge)}
                                        </Col>
                                        <Col xs={2}>
                                            {renderCostCell(charge)}
                                        </Col>
                                        <Col xs={1} style={{ display: 'flex', justifyContent: 'center' }}>
                                            <div>
                                                <span className={styles.headerButton}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        if (charge.collapsed) {
                                                            charge.collapsed = !charge.collapsed;
                                                        }
                                                        geofences.append({ chargeId: charge.id, percentage: 0, km: 0, duration: 0 })
                                                    }} >
                                                    <span className={styles.headerButtonText}></span>
                                                    <img src={AddIcon} className={styles.headerButtonIcon} />
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                    <ReactTooltip />

                                    {!charge.collapsed &&
                                        <div key={`r_charge_${charge.id}_${geofences.fields.length}`} >
                                            {/* header */}
                                            <Row className={styles.lineGeofenceTitle} >
                                                <Col xs={1} />
                                                <Col xs={6}>
                                                    {t('charges.list.geofences')}
                                                </Col>
                                                <Col xs={2} >
                                                    {calculation === 'Km' ? t('charges.geofences.kms') : t('charges.geofences.duration')}
                                                </Col>
                                                <Col xs={3} >
                                                    {t('charges.geofences.percentage')}
                                                </Col>
                                            </Row>

                                            {/* geofences */}
                                            {geofences.fields.map((g, i) => (g.chargeId === charge.id ? i : -1))
                                                .filter(i => i !== -1)
                                                .map(i => {
                                                    return (
                                                        <Row className={styles.lineGeofence} key={geofences.fields[i].formId}>
                                                            <Col xs={1} />
                                                            <Col xs={6}>
                                                                <div style={{ paddingRight: '2rem' }}>
                                                                    <Controller
                                                                        render={({ value }) => {
                                                                            return (
                                                                                <Select
                                                                                    options={geofenceList}
                                                                                    isClearable
                                                                                    placeholder={t('charges.geofences.geofence')}
                                                                                    onChange={(e: SelectValueLabel) => {
                                                                                        form.setValue(`geofences[${i}].geofenceId`, e ? e.value : null);
                                                                                    }}
                                                                                    isDisabled={false}
                                                                                    value={geofenceList.find(x => x.value === value) ? geofenceList.find(x => x.value === value) : null}
                                                                                />
                                                                            );
                                                                        }}
                                                                        rules={{ required: true }}
                                                                        control={form.control}
                                                                        name={`geofences[${i}].geofenceId`}
                                                                        defaultValue={form.getValues(`geofences[${i}].geofenceId`)} />
                                                                    <InputError error={errors.geofences?.[i]?.geofenceId} />
                                                                </div>
                                                            </Col>
                                                            <Col xs={2} >

                                                                {calculation === 'Km' ?
                                                                    <InputGroup
                                                                        format="number"
                                                                        text="km"
                                                                        style={{ textAlign: 'end' }}
                                                                        placeholder={t('charges.geofences.kms')}
                                                                        value={form.getValues(`geofences[${i}].km`)}
                                                                        disabled={true}
                                                                    /> :
                                                                    <InputGroup
                                                                        format="number"
                                                                        text="min."
                                                                        style={{ textAlign: 'end' }}
                                                                        placeholder={t('charges.geofences.duration')}
                                                                        value={(form.getValues(`geofences[${i}].duration`) as number ?? 0) / 60}
                                                                        disabled={true}
                                                                    />
                                                                }

                                                            </Col>
                                                            <Col xs={2} >
                                                                <Controller
                                                                    render={({ }) => {
                                                                        return (
                                                                            <InputGroup
                                                                                style={{ textAlign: 'end' }} format={'number'}
                                                                                placeholder={t('charges.geofences.percentage')}
                                                                                text={'%'}
                                                                                value={form.getValues(`geofences[${i}].percentage`)}
                                                                                onValueChange={value => {
                                                                                    form.setValue(`geofences[${i}].percentage`, value);
                                                                                    setReRender(reRender + 1);
                                                                                }}
                                                                                min={0}
                                                                                max={100}
                                                                                disabled={false} />
                                                                        );
                                                                    }}
                                                                    control={form.control}
                                                                    name={`geofences[${i}].percentage`}
                                                                    defaultValue={form.getValues(`geofences[${i}].percentage`)}
                                                                    rules={{
                                                                        min: { value: Number.EPSILON, message: t('charges.geofences.min_value_greater_than_0' as any) },
                                                                        max: { value: 100, message: t('common.errors.max_value_100') }
                                                                    }}
                                                                />
                                                                <InputError error={errors.geofences?.[i]?.percentage} />
                                                            </Col>
                                                            <Col xs={1} >
                                                                <div className={styles.removeButtonContainer}>
                                                                    <div className={styles.removeButton} onClick={e => {
                                                                        e.stopPropagation();
                                                                        setGeofenseToRemove(i);
                                                                        setShowRemoveGeofenceModal(true);
                                                                    }} >
                                                                        <TrashIcon className={styles.removeIcon} />
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>)
                                                })}


                                            {geofences.fields.filter(g => g.chargeId === charge.id).length === 0 ?

                                                //sem dados
                                                <Row className={styles.lineGeofenceTitle} style={{ justifyContent: 'center', fontWeight: 'normal' }}>
                                                    {t('common.without_elements')}
                                                </Row>
                                                :
                                                //total 
                                                <Row className={styles.lineGeofenceTitle} >
                                                    <Col xs={1} />

                                                    <Col xs={8} style={{ display: 'flex', justifyContent: 'right', paddingRight: '3rem' }}>
                                                        {t('common.total')}
                                                    </Col>
                                                    <Col xs={2} >
                                                        <InputGroup
                                                            format="number"
                                                            text="%"
                                                            style={{ textAlign: 'end' }}
                                                            placeholder={t('charges.geofences.percentage_label')}
                                                            value={(Math.round((form.watch('geofences') || []).filter(g => g.chargeId === charge.id).reduce((sum, g) => sum + (Number(g.percentage) || 0), 0) * 100) / 100)}
                                                            min={0}
                                                            max={100}
                                                            disabled={true}
                                                            warningBorder={charge.percentageOverLimit}
                                                        />
                                                    </Col>
                                                    <Col xs={1} />
                                                </Row>
                                            }
                                        </div>}
                                </>
                            })
                            }

                            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveGeofenceModal} message={t('common.messages.remove_record_with_ident')} />

                            <PaginationWithInfo
                                itemName={t('charges.list.charges')}
                                currentPage={currentPage}
                                pageItems={paginatedCharges.length}
                                totalItems={totalItems}
                                onChange={onPageChange}
                            />

                        </Box>
                    </div>
                </form>
        </CompactPageLayout>
    );
}

export default ChargeGeofenceScreen;
